import classnames from 'classnames'
import React, { useEffect, useRef } from 'react'

interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  // Maximum number of lines before scrolling.
  maxLines?: number
  // Minimum number of lines for the textarea.
  minLines?: number
}

/**
 * Allows textarea to grow as new text is entered.
 */
export const Textarea: React.FC<TextareaProps> = ({
  className,
  maxLines = 1,
  minLines = 1,
  ...props
}) => {
  const textEl = useRef<HTMLTextAreaElement>(null)

  // Cache heights in pixels to avoid calling getComputedStyle every render.
  const heightRange = useRef<[number, number] | null>(null)
  useEffect(() => {
    const textareaNode = textEl.current!
    const textareaStyles = window.getComputedStyle(textareaNode)
    const paddingOffset =
      parseFloat(textareaStyles.paddingTop) +
      parseFloat(textareaStyles.paddingBottom)
    const lineHeight = parseFloat(textareaStyles.lineHeight)

    const minHeight = minLines * lineHeight + paddingOffset
    const maxHeight = maxLines && maxLines * lineHeight + paddingOffset
    heightRange.current = [minHeight, maxHeight]
  }, [minLines, maxLines])

  useEffect(() => {
    const textareaNode = textEl.current!
    const [minHeight, maxHeight] = heightRange.current!
    const resizedMaxHeight = maxHeight
      ? Math.min(textareaNode.scrollHeight, maxHeight)
      : textareaNode.scrollHeight
    const height = Math.max(resizedMaxHeight, minHeight)

    textareaNode.classList.toggle(
      'vd-textarea--scrollable',
      resizedMaxHeight === maxHeight
    )

    textareaNode.style.height = `${height}px`
  })

  return (
    <textarea
      {...props}
      className={classnames('vd-input', 'vd-textarea', className)}
      ref={textEl}
    />
  )
}
